import { createSlice } from '@reduxjs/toolkit';
import { apiSlice } from '../../app/apiSlice';

// RTK Query API endpoints
export const classesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Live Classes endpoints
    createLiveClass: builder.mutation({
      query: (data) => ({
        url: '/api/v1/en/admin/live-class/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['LiveClass', 'ClassRange'],
    }),

    getLiveClass: builder.query({
      query: (id) => `/api/v1/en/admin/live-class/view/${id}`,
      transformResponse: (response) => {
        // Return the live class data from the nested structure
        return response?.data?.data?.liveClass || {};
      },
      providesTags: (result, error, id) => [{ type: 'LiveClass', id }],
    }),

    getLiveClasses: builder.query({
      query: (params = {}) => {
        const queryParts = [];
        if (params.courseId) queryParts.push(`courseId=${params.courseId}`);
        if (params.courseModuleId) {
          // Handle both single moduleId and array of moduleIds (for "all" case)
          if (Array.isArray(params.courseModuleId)) {
            queryParts.push(`courseModuleId=${params.courseModuleId.join(',')}`);
          } else {
            queryParts.push(`courseModuleId=${params.courseModuleId}`);
          }
        }
        if (params.limit) queryParts.push(`limit=${params.limit}`);
        if (params.offset) queryParts.push(`offset=${params.offset}`);

        // Add filter parameters - API expects startTime and endTime
        if (params.startTime) queryParts.push(`startTime=${params.startTime}`);
        if (params.endTime) queryParts.push(`endTime=${params.endTime}`);
        if (params.status && params.status !== 'all') queryParts.push(`status=${params.status}`);

        const queryString = queryParts.join('&');
        return `/api/v1/en/admin/live-class/list?${queryString}`;
      },
      transformResponse: (response) => {
        // Return the full response structure with classes array
        return response?.data?.data || { classes: [], countDocuments: 0 };
      },
      providesTags: ['LiveClass'],
    }),

    // Class Ranges endpoints - New addition for listing class ranges
    getClassRanges: builder.query({
      query: (params = {}) => {
        const queryParts = [];
        if (params.courseId) queryParts.push(`courseId=${params.courseId}`);
        if (params.courseModuleId) {
          // Handle both single moduleId and array of moduleIds (for "all" case)
          if (Array.isArray(params.courseModuleId)) {
            queryParts.push(`courseModuleId=${params.courseModuleId.join(',')}`);
          } else {
            queryParts.push(`courseModuleId=${params.courseModuleId}`);
          }
        }
        if (params.limit) queryParts.push(`limit=${params.limit}`);
        if (params.offset) queryParts.push(`offset=${params.offset}`);

        // Add filter parameters for class ranges
        if (params.startTime) queryParts.push(`startTime=${params.startTime}`);
        if (params.endTime) queryParts.push(`endTime=${params.endTime}`);
        if (params.status && params.status !== 'all') queryParts.push(`status=${params.status}`);

        const queryString = queryParts.join('&');
        return `/api/v1/en/admin/live-class/list?${queryString}`;
      },
      transformResponse: (response) => {
        // Return the classes array as ranges - based on the API response structure
        return response?.data?.data || { classes: [], countDocuments: 0 };
      },
      providesTags: ['ClassRange'],
    }),

    // View Class Range - Get multiple classes within a range
    getClassRange: builder.query({
      query: (id) => `/api/v1/en/admin/live-class/range/view/${id}`,
      transformResponse: (response) => {
        // Extract classIds from liveClassRange
        const liveClassRange = response?.data?.data?.liveClassRange || {};
        return {
          ...liveClassRange,
          classes: liveClassRange.classIds || [],
        };
      },
      providesTags: (result, error, id) => [{ type: 'ClassRange', id }],
    }),

    // Update Class Range
    updateClassRange: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/en/admin/live-class/range/update/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'ClassRange', id }, 'ClassRange'],
    }),

    // Delete Class Range
    deleteClassRange: builder.mutation({
      query: (id) => ({
        url: `/api/v1/en/admin/live-class/range/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ClassRange'],
    }),

    // Individual Class Operations
    // View Individual Class
    getIndividualClass: builder.query({
      query: (id) => `/api/v1/en/admin/live-class/view/${id}`,
      transformResponse: (response) => {
        return response?.data?.data || {};
      },
      providesTags: (result, error, id) => [{ type: 'LiveClass', id }],
    }),

    // Update Individual Class
    updateIndividualClass: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/en/admin/live-class/update/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'LiveClass', id },
        'LiveClass',
        'ClassRange', // Also invalidate class ranges since individual classes belong to ranges
      ],
    }),

    // Delete Individual Class
    deleteIndividualClass: builder.mutation({
      query: (id) => ({
        url: `/api/v1/en/admin/live-class/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        'LiveClass',
        'ClassRange', // Also invalidate class ranges since individual classes belong to ranges
      ],
    }),

    updateLiveClass: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/en/admin/live-class/update/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'LiveClass', id }],
    }),

    deleteLiveClass: builder.mutation({
      query: (id) => ({
        url: `/api/v1/en/admin/live-class/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LiveClass'],
    }),

    // Recorded Lectures endpoints
    createLecture: builder.mutation({
      query: (data) => ({
        url: '/api/v1/en/admin/lectures/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Lecture'],
    }),

    getLecture: builder.query({
      query: (id) => `/api/v1/en/admin/lectures/view/${id}`,
      transformResponse: (response) => {
        // Return the lecture data
        return response?.data?.data || {};
      },
      providesTags: (result, error, id) => [{ type: 'Lecture', id }],
    }),

    uploadLecture: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/api/v1/en/admin/lectures/upload/${id}`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Lecture', id }],
    }),

    // Upload handout PDFs for lecture
    uploadLectureHandout: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/api/v1/en/admin/lectures/upload/handout/${id}?uploadPdfs`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Lecture', id }],
    }),

    // Upload notes for lecture
    uploadLectureNotes: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/api/v1/en/admin/lectures/upload/notes/${id}`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Lecture', id }],
    }),

    // Delete handout from lecture
    deleteLectureHandout: builder.mutation({
      query: ({ lectureId, handoutId }) => ({
        url: `/api/v1/en/admin/lectures/${lectureId}/handouts/${handoutId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { lectureId }) => [{ type: 'Lecture', id: lectureId }],
    }),

    // Delete notes from lecture
    deleteLectureNotes: builder.mutation({
      query: ({ lectureId, notesId }) => ({
        url: `/api/v1/en/admin/lectures/${lectureId}/notes/${notesId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { lectureId }) => [{ type: 'Lecture', id: lectureId }],
    }),

    // Update video content for existing lecture
    updateLectureVideo: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/api/v1/en/admin/lectures/update-video/${id}`,
        method: 'PATCH',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Lecture', id }],
    }),

    updateLecture: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/en/admin/lectures/update/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Lecture', id }],
    }),

    getLectures: builder.query({
      query: (params = {}) => {
        const queryParts = [];
        if (params.courseId) queryParts.push(`courseId=${params.courseId}`);
        if (params.courseModuleId) {
          // Handle both single moduleId and array of moduleIds (for "all" case)
          if (Array.isArray(params.courseModuleId)) {
            queryParts.push(`courseModuleId=${params.courseModuleId.join(',')}`);
          } else {
            queryParts.push(`courseModuleId=${params.courseModuleId}`);
          }
        }

        // Add filter parameters - keeping startDate/endDate for lectures API
        if (params.startDate) queryParts.push(`startDate=${params.startDate}`);
        if (params.endDate) queryParts.push(`endDate=${params.endDate}`);
        if (params.status && params.status !== 'all') queryParts.push(`status=${params.status}`);

        // Add pagination parameters
        const limit = params.limit ?? 12;
        const offset = params.offset ?? 0;
        queryParts.push(`limit=${limit}`);
        queryParts.push(`offset=${offset}`);

        const queryString = queryParts.join('&');
        return `/api/v1/en/admin/lectures/list?${queryString}`;
      },
      transformResponse: (response) => {
        // Return the full response structure with lectures array and total count
        return response?.data?.data || { lectures: [], total: 0, dataCount: 0 };
      },
      providesTags: ['Lecture'],
    }),

    deleteLecture: builder.mutation({
      query: (id) => ({
        url: `/api/v1/en/admin/lectures/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Lecture'],
    }),

    // Status Management APIs - Using update endpoints
    updateLiveClassStatus: builder.mutation({
      query: ({ id, status, rejectReason }) => ({
        url: `/api/v1/en/admin/live-class/update/${id}`,
        method: 'PATCH',
        body: {
          status,
          ...(rejectReason && { rejectReason }),
        },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'LiveClass', id }, 'LiveClass'],
    }),

    // Update Class Range Status with multiple class IDs
    updateClassRangeStatus: builder.mutation({
      query: ({ classIds, status, rejectReason }) => ({
        url: '/api/v1/en/admin/live-class/update/status',
        method: 'PATCH',
        body: {
          classIds,
          status,
          ...(rejectReason && { rejectReason }),
        },
      }),
      invalidatesTags: ['LiveClass', 'ClassRange'],
    }),

    updateLectureStatus: builder.mutation({
      query: ({ id, status, rejectReason }) => ({
        url: `/api/v1/en/admin/lectures/update/${id}`,
        method: 'PATCH',
        body: {
          status,
          ...(rejectReason && { rejectReason }),
        },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Lecture', id }, 'Lecture'],
    }),

    getCoursesForClasses: builder.query({
      query: () => '/api/v1/en/admin/course/list-all',
      transformResponse: (response) => {
        console.log('Classes - Courses API Response:', response);
        // Based on the API response structure: data.data.packages
        return response?.data?.data?.packages || [];
      },
      providesTags: ['Course'],
    }),
    updateClassRangeById: builder.mutation({
      query: ({ id, status, rejectReason }) => ({
        url: `/api/v1/en/admin/live-class/range/update/${id}`,
        method: 'PATCH',
        body: {
          status,
          ...(rejectReason && { rejectReason }),
        },
      }),
      invalidatesTags: ['ClassRange', 'LiveClass'],
    }),
    // List timezones
    listTimezones: builder.query({
      query: ({ limit = 500 } = {}) => `/api/v1/en/timezone/list-all?limit=${limit}`,
      transformResponse: (response) => {
        // The correct path is response.data.data.timeZone.timeZone
        const timezones = response?.data?.data?.timeZone?.timeZone || [];
        return Array.isArray(timezones) ? timezones : [];
      },
      providesTags: ['Timezones'],
    }),

    // Course Material endpoints
    uploadCourseMaterial: builder.mutation({
      query: ({ courseId, formData }) => ({
        url: '/api/v1/en/admin/course-material/upload',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['CourseMaterial'],
    }),

    getCourseMaterials: builder.query({
      query: (params = {}) => {
        const queryParts = [];
        if (params.courseId) queryParts.push(`courseId=${params.courseId}`);
        if (params.courseModuleId) {
          if (Array.isArray(params.courseModuleId)) {
            queryParts.push(`courseModuleId=${params.courseModuleId.join(',')}`);
          } else {
            queryParts.push(`courseModuleId=${params.courseModuleId}`);
          }
        }

        // Add filter parameters
        if (params.startDate) queryParts.push(`startDate=${params.startDate}`);
        if (params.endDate) queryParts.push(`endDate=${params.endDate}`);
        if (params.status && params.status !== 'all') queryParts.push(`status=${params.status}`);

        // Add pagination parameters
        const limit = params.limit ?? 12;
        const offset = params.offset ?? 0;
        queryParts.push(`limit=${limit}`);
        queryParts.push(`offset=${offset}`);

        const queryString = queryParts.join('&');
        return `/api/v1/en/admin/course-material/list?${queryString}`;
      },
      transformResponse: (response) => {
        // API returns courseMaterials array and total
        const data = response?.data?.data || {};
        return {
          courseMaterials: data.courseMaterials || [],
          total: data.total || 0,
        };
      },
      providesTags: ['CourseMaterial'],
    }),

    getCourseMaterial: builder.query({
      query: (id) => `/api/v1/en/admin/course-material/view/${id}`,
      transformResponse: (response) => {
        return response?.data?.data || {};
      },
      providesTags: (result, error, id) => [{ type: 'CourseMaterial', id }],
    }),

    updateCourseMaterial: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/en/admin/course-material/update/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'CourseMaterial', id },
        'CourseMaterial',
      ],
    }),

    deleteCourseMaterial: builder.mutation({
      query: (id) => ({
        url: `/api/v1/en/admin/course-material/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CourseMaterial'],
    }),

    updateCourseMaterialStatus: builder.mutation({
      query: ({ id, status, rejectReason }) => ({
        url: `/api/v1/en/admin/course-material/update/${id}`,
        method: 'PATCH',
        body: {
          status,
          ...(rejectReason && { rejectReason }),
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'CourseMaterial', id },
        'CourseMaterial',
      ],
    }),
  }),
});

export const {
  useCreateLiveClassMutation,
  useGetLiveClassQuery,
  useGetLiveClassesQuery,
  useGetClassRangesQuery,
  useGetClassRangeQuery,
  useUpdateClassRangeMutation,
  useDeleteClassRangeMutation,
  useGetIndividualClassQuery,
  useUpdateIndividualClassMutation,
  useDeleteIndividualClassMutation,
  useUpdateLiveClassMutation,
  useDeleteLiveClassMutation,
  useUpdateLiveClassStatusMutation,
  useUpdateClassRangeStatusMutation,
  useUpdateClassRangeByIdMutation,
  useCreateLectureMutation,
  useGetLectureQuery,
  useUploadLectureMutation,
  useUploadLectureHandoutMutation,
  useUploadLectureNotesMutation,
  useDeleteLectureHandoutMutation,
  useDeleteLectureNotesMutation,
  useUpdateLectureVideoMutation,
  useUpdateLectureMutation,
  useGetLecturesQuery,
  useDeleteLectureMutation,
  useUpdateLectureStatusMutation,
  useGetCoursesForClassesQuery,
  useListTimezonesQuery,
  // Course Material hooks
  useUploadCourseMaterialMutation,
  useGetCourseMaterialsQuery,
  useGetCourseMaterialQuery,
  useUpdateCourseMaterialMutation,
  useDeleteCourseMaterialMutation,
  useUpdateCourseMaterialStatusMutation,
} = classesApiSlice;

export const classesSlice = createSlice({
  name: 'classes',
  initialState: {
    liveClasses: [],
    recordedLectures: [],
    loading: false,
    error: null,
    activeTab: 'live', // 'live' or 'recorded'
    activeModuleTab: 'listening',
    activeViewTab: 'cards',
    filters: {
      status: 'all',
      level: 'all',
      instructor: 'all',
      category: 'all',
    },
    pagination: {
      currentPage: 1,
      totalPages: 1,
      itemsPerPage: 12,
    },
  },
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    setError: (state, action) => {
      state.error = action.payload;
    },

    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },

    setActiveModuleTab: (state, action) => {
      state.activeModuleTab = action.payload;
    },

    setActiveViewTab: (state, action) => {
      state.activeViewTab = action.payload;
    },

    setLiveClasses: (state, action) => {
      state.liveClasses = action.payload;
    },

    setRecordedLectures: (state, action) => {
      state.recordedLectures = action.payload;
    },

    addLiveClass: (state, action) => {
      state.liveClasses.push(action.payload);
    },

    updateLiveClass: (state, action) => {
      const index = state.liveClasses.findIndex((cls) => cls.id === action.payload.id);
      if (index !== -1) {
        state.liveClasses[index] = action.payload;
      }
    },

    removeLiveClass: (state, action) => {
      state.liveClasses = state.liveClasses.filter((cls) => cls.id !== action.payload);
    },

    addRecordedLecture: (state, action) => {
      state.recordedLectures.push(action.payload);
    },

    updateRecordedLecture: (state, action) => {
      const index = state.recordedLectures.findIndex((lec) => lec.id === action.payload.id);
      if (index !== -1) {
        state.recordedLectures[index] = action.payload;
      }
    },

    removeRecordedLecture: (state, action) => {
      state.recordedLectures = state.recordedLectures.filter((lec) => lec.id !== action.payload);
    },

    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    resetFilters: (state) => {
      state.filters = {
        status: 'all',
        level: 'all',
        instructor: 'all',
        category: 'all',
      };
    },
  },
});

export const {
  setLoading,
  setError,
  setActiveTab,
  setActiveModuleTab,
  setActiveViewTab,
  setLiveClasses,
  setRecordedLectures,
  addLiveClass,
  updateLiveClass,
  removeLiveClass,
  addRecordedLecture,
  updateRecordedLecture,
  removeRecordedLecture,
  setFilters,
  setPagination,
  resetFilters,
} = classesSlice.actions;

export default classesSlice.reducer;
